import React, { useState, useEffect } from 'react';
import { FaBell, FaTrash, FaExclamationTriangle, FaSpinner, FaShieldAlt, FaEye, FaEnvelope, FaLock } from 'react-icons/fa';
import api from '../../services/api';
import CustomDropdown from '../common/UserCustomDropdown';
import BlockedUsers from './BlockedUsers';

const Settings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState(null);
    const [notifications, setNotifications] = useState({
        email: false,
        sms: false,
        push: false,
        matches: false
    });
    const [privacy, setPrivacy] = useState({
        profileVisibility: 'everyone',
        showOnlineStatus: true
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [otherReason, setOtherReason] = useState('');
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // Error states
    const [passwordErrors, setPasswordErrors] = useState({});
    const [deleteErrors, setDeleteErrors] = useState({});
    const [generalError, setGeneralError] = useState('');

    const visibilityOptions = [
        { value: 'everyone', label: 'Everyone' },
        { value: 'registered', label: 'Registered Users' },
        { value: 'premium', label: 'Premium Members Only' },
    ];

    const deleteReasonOptions = [
        { value: '', label: 'Select a reason' },
        { value: 'Found a match', label: 'I found my match' },
        { value: 'Not satisfied', label: 'Not satisfied with the service' },
        { value: 'Privacy concerns', label: 'Privacy concerns' },
        { value: 'Too expensive', label: 'Too expensive' },
        { value: 'Other', label: 'Other' },
    ];

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/settings');
            setSettings(response.data);
            setNotifications(response.data.notifications);
            setPrivacy(response.data.privacy);
        } catch (error) {
            console.error('Error fetching settings:', error);
            setGeneralError('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const toggle = async (key) => {
        const newNotifications = { ...notifications, [key]: !notifications[key] };
        setNotifications(newNotifications);

        try {
            await api.put('/settings/notifications', newNotifications);
        } catch (error) {
            console.error('Error updating notifications:', error);
            setNotifications(notifications);
            setGeneralError('Failed to update notification settings');
        }
    };

    const handleVisibilityChange = async (value) => {
        const newVisibility = value;  // value is passed directly by CustomDropdown
        setPrivacy({ ...privacy, profileVisibility: newVisibility });

        try {
            await api.put('/settings/privacy', { profileVisibility: newVisibility });
        } catch (error) {
            console.error('Error updating privacy:', error);
            setGeneralError('Failed to update privacy settings');
        }
    };

    const handleChangePassword = async () => {
        setPasswordErrors({});

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordErrors({ confirm: 'New passwords do not match' });
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordErrors({ new: 'Password must be at least 6 characters' });
            return;
        }

        setSaving(true);
        try {
            const response = await api.put('/settings/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });

            setGeneralError('');
            setShowPasswordModal(false);
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            console.error('Error changing password:', error);
            const msg = error.response?.data?.message || 'Failed to change password';
            setPasswordErrors({ general: msg });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleteErrors({});

        if (!deleteReason) {
            setDeleteErrors({ reason: 'Please select a reason for deleting your account.' });
            return;
        }
        if (deleteReason === 'Other' && !otherReason.trim()) {
            setDeleteErrors({ other: 'Please specify the reason.' });
            return;
        }

        setSaving(true);
        try {
            const response = await api.delete('/settings/delete-account', {
                data: { reason: deleteReason, otherReason }
            });

            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/';
        } catch (error) {
            console.error('Error deleting account:', error);
            setDeleteErrors({ general: 'Failed to delete account' });
            setSaving(false);
        }
    };

    const toggleTwoFactor = async () => {
        try {
            const newValue = !settings.security.twoFactorEnabled;
            const response = await api.post('/settings/toggle-2fa', { enabled: newValue });

            setSettings({
                ...settings,
                security: {
                    ...settings.security,
                    twoFactorEnabled: newValue
                }
            });
        } catch (error) {
            console.error('Error toggling 2FA:', error);
            setGeneralError('Failed to update two-factor authentication');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <FaSpinner className="animate-spin h-12 w-12 text-rose-600 mx-auto" />
                    <p className="mt-4 text-gray-600">Loading settings...</p>
                </div>
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="p-6 text-center">
                <p className="text-red-600">{generalError || 'Failed to load settings'}</p>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Settings</h1>
                        <p className="text-gray-600">
                            Manage your account preferences, privacy, and security settings
                        </p>
                    </div>


                </div>
                {generalError && <p className="text-red-600 text-sm">{generalError}</p>}
            </div>

            {/* Settings Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Security Settings */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <FaShieldAlt className="text-rose-600 text-xl" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Security</h3>
                    </div>

                    <div className="space-y-6">
                        {/* Password */}
                        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                            <div>
                                <h4 className="font-medium text-gray-800">Password</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                    Last changed {settings.security.lastPasswordChange
                                        ? new Date(settings.security.lastPasswordChange).toLocaleDateString()
                                        : 'Never'}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowPasswordModal(true);
                                    setPasswordErrors({});
                                    setGeneralError('');
                                }}
                                className="px-4 py-2 bg-rose-600 text-white text-sm rounded-lg hover:bg-rose-700"
                            >
                                Change
                            </button>
                        </div>

                        {/* Two-Factor Authentication */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="font-medium text-gray-800">Two-Factor Authentication</h4>
                                <p className="text-sm text-gray-500 mt-1">Extra security for your account</p>
                            </div>
                            
                            {/* Action Toggle (Locked) */}
                            <button
                                disabled={true}
                                title="Premium Feature Locked"
                                className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-not-allowed items-center rounded-full border-2 border-transparent transition-colors duration-300 opacity-70 ${
                                    settings.security.twoFactorEnabled ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                            >
                                <span className={`flex items-center justify-center h-6 w-6 transform rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-300 ${
                                    settings.security.twoFactorEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`}>
                                    <FaLock className="text-amber-400 text-[11px]" />
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Notification Settings */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <FaBell className="text-blue-600 text-xl" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                    </div>

                    <div className="space-y-6">
                        <ToggleItem
                            label="Email Notifications"
                            description="Receive updates via email"
                            checked={notifications.email}
                            onChange={() => toggle('email')}
                            icon={<FaEnvelope className="text-gray-400" />}
                        />
                        <ToggleItem
                            label="SMS Notifications"
                            description="Receive updates via SMS"
                            checked={notifications.sms}
                            onChange={() => toggle('sms')}
                            icon={<FaEnvelope className="text-gray-400" />}
                        />
                        <ToggleItem
                            label="New Matches"
                            description="Notify when new matches are found"
                            checked={notifications.matches}
                            onChange={() => toggle('matches')}
                            icon={<FaBell className="text-gray-400" />}
                        />
                    </div>
                </div>

                {/* Privacy Settings */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <FaEye className="text-green-600 text-xl" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Privacy & Blocking</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Profile Visibility */}
                        <div>
                            <h4 className="font-medium text-gray-800 mb-3">Profile Visibility</h4>
                            <p className="text-sm text-gray-500 mb-4">Control who can see your profile</p>
                            <CustomDropdown
                                options={visibilityOptions}
                                value={privacy.profileVisibility}

                                onChange={handleVisibilityChange}
                                placeholder="Select visibility"
                                searchable={false}
                            />
                        </div>

                        {/* Blocked Users */}
                        <div>
                            <h4 className="font-medium text-gray-800 mb-3">Blocked Users</h4>
                            <p className="text-sm text-gray-500 mb-4">Manage users you've blocked</p>
                            <BlockedUsers />
                        </div>
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-lg border border-red-200 p-6 lg:col-span-2">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <FaTrash className="text-red-600 text-xl" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Account Management</h3>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h4 className="font-medium text-gray-800 mb-2">Delete Account</h4>
                            <p className="text-sm text-gray-500">
                                Permanently delete your account and all associated data
                            </p>
                        </div>
                        <button
                            onClick={() => {
                                setShowDeleteModal(true);
                                setDeleteErrors({});
                            }}
                            className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 whitespace-nowrap"
                        >
                            Delete Account
                        </button>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Change Password</h3>

                        {passwordErrors.general && (
                            <p className="text-red-600 text-sm mb-4">{passwordErrors.general}</p>
                        )}

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                                <input
                                    type="password"
                                    value={passwordData.currentPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                    className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                                <input
                                    type="password"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                                />
                                {passwordErrors.new && <p className="text-red-600 text-xs mt-1">{passwordErrors.new}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                                <input
                                    type="password"
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                    className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                                />
                                {passwordErrors.confirm && <p className="text-red-600 text-xs mt-1">{passwordErrors.confirm}</p>}
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowPasswordModal(false);
                                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                                    setPasswordErrors({});
                                }}
                                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleChangePassword}
                                className="flex-1 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center justify-center"
                                disabled={saving}
                            >
                                {saving ? <FaSpinner className="animate-spin" /> : 'Change Password'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600 text-2xl">
                                <FaExclamationTriangle />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Account?</h3>
                            <p className="text-gray-500 text-sm">
                                This action cannot be undone. All your data will be permanently removed.
                            </p>
                        </div>

                        {deleteErrors.general && (
                            <p className="text-red-600 text-sm mb-4 text-center">{deleteErrors.general}</p>
                        )}

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-3">Why are you leaving?</label>
                            <CustomDropdown
                                options={deleteReasonOptions}
                                value={deleteReason}
                                onChange={(value) => {
                                    setDeleteReason(value);
                                    setDeleteErrors({});
                                }}
                                placeholder="Select a reason"
                                searchable={false}
                            />
                            {deleteErrors.reason && <p className="text-red-600 text-xs mt-1">{deleteErrors.reason}</p>}

                            {deleteReason === 'Other' && (
                                <>
                                    <textarea
                                        placeholder="Please tell us more..."
                                        value={otherReason}
                                        onChange={(e) => setOtherReason(e.target.value)}
                                        className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm h-24 resize-none mt-3"
                                    ></textarea>
                                    {deleteErrors.other && <p className="text-red-600 text-xs mt-1">{deleteErrors.other}</p>}
                                </>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteReason('');
                                    setOtherReason('');
                                    setDeleteErrors({});
                                }}
                                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center"
                                disabled={saving}
                            >
                                {saving ? <FaSpinner className="animate-spin" /> : 'Delete Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ToggleItem = ({ label, description, checked, onChange, icon }) => (
    <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
            {icon}
            <div>
                <h4 className="font-medium text-gray-800 text-sm">{label}</h4>
                <p className="text-xs text-gray-500 mt-1">{description}</p>
            </div>
        </div>
        <button
            disabled={true}
            title="Premium Feature Locked"
            className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-not-allowed items-center rounded-full border-2 border-transparent transition-colors duration-300 opacity-70 ${
                checked ? 'bg-green-500' : 'bg-gray-300'
            }`}
        >
            <span className={`flex items-center justify-center h-6 w-6 transform rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-300 ${
                checked ? 'translate-x-5' : 'translate-x-0'
            }`}>
                <FaLock className="text-amber-400 text-[11px]" />
            </span>
        </button>
    </div>
);

export default Settings;